const BASE_POLYGON_FILTER_KEYS = [
  "minPrice",
  "maxPrice",
  "minPeople",
  "maxPeople",
  "workspaceType",
  "amenities",
  "facilities",
  "promo",
  "workspaceId",
  "sortBy",
  "sortOrder",
  "order",
  "page",
  // Free-text keyword — forwarded to location-polygon so results (polygon or,
  // once the map moves, bounds) stay filtered by it as the user pans.
  "q",
] as const;

const VIEWPORT_POLYGON_KEYS = [
  "minLatitude",
  "maxLatitude",
  "minLongitude",
  "maxLongitude",
  "latitude",
  "longitude",
  "zoom",
] as const;

export function hasViewportBoundsInSearchParams(searchParams: URLSearchParams) {
  return VIEWPORT_POLYGON_KEYS.slice(0, 4).some((key) => searchParams.has(key));
}

export function buildGeoSearchPinsQueryString(apiQueryString: string): string {
  const params = new URLSearchParams(
    apiQueryString.startsWith("?") ? apiQueryString.slice(1) : apiQueryString,
  );
  params.delete("page");
  params.delete("perPage");
  params.set("onlyPins", "true");
  return params.toString();
}

/** Listing filters to forward when fetching a single property for the map popup. */
export function buildPropertyDetailsFilterQuery(query?: string): string {
  if (!query) return "";
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
  const out = new URLSearchParams();
  for (const key of BASE_POLYGON_FILTER_KEYS) {
    if (key === "page") continue;
    const value = params.get(key);
    if (value == null || value === "" || value === "reset" || value === "All") continue;
    out.set(key, value);
  }
  return out.toString();
}

function slimListingAssets(assets: unknown, max = 5) {
  if (!Array.isArray(assets)) return [];
  return assets
    .slice(0, max)
    .map((asset: any) => {
      const url = asset?.url || asset?.image || "";
      return url ? { url } : null;
    })
    .filter(Boolean) as { url: string }[];
}

/** Map pins only need coordinates, price, and identity — not full listing blobs. */
export function normalizeMapPinRecord(pin: any) {
  const lat = pin?.geolocationLat ?? pin?.lat ?? pin?.latitude;
  const lng = pin?.geolocationLong ?? pin?.lng ?? pin?.longitude;
  return {
    id: pin?.id ?? pin?.propertyId ?? pin?.sfid,
    propertyId: pin?.propertyId ?? pin?.id ?? pin?.sfid,
    sfid: pin?.sfid,
    geolocationLat: lat,
    geolocationLong: lng,
    priceMonthly: pin?.priceMonthly ?? pin?.advertisedPriceMonthly,
    advertisedPriceMonthly: pin?.advertisedPriceMonthly,
    avgMonthlyCostPerWorkstation: pin?.avgMonthlyCostPerWorkstation ?? pin?.costPerWorkstation,
    currencyIsoCode: pin?.currencyIsoCode,
    priceTier: pin?.priceTier ?? pin?.price_tier ?? null,
  };
}

function slimBuildingData(building: any) {
  if (!building || typeof building !== "object") return undefined;
  return {
    id: building.id,
    sfid: building.sfid,
    name: building.name,
    street: building.street,
    suburb: building.suburb,
    state: building.state,
    country: building.country,
  };
}

function slimWorkspaceData(workspace: any) {
  if (!workspace || typeof workspace !== "object") return undefined;
  return {
    id: workspace.id,
    sfid: workspace.sfid,
    name: workspace.name,
    country: workspace.country,
    partnerFavicon: workspace.partnerFavicon,
  };
}

function slimPropertyData(property: any) {
  if (!property || typeof property !== "object") return undefined;
  return {
    id: property.id ?? property.propertyId ?? property.sfid,
    sfid: property.sfid,
    hideStreetNumber: property.hideStreetNumber,
    streetNumber: property.streetNumber,
    streetName: property.streetName,
    city: property.city,
    state: property.state,
    state_picklist__c: property.state_picklist__c,
    country: property.country,
  };
}

/** Drop unused API fields so Remix HTML / hydration JSON stays small. */
export function slimSearchListingRecord(listing: any) {
  if (!listing || typeof listing !== "object") return listing;
  const workspaceProvider = listing.workspaceProvider;
  return {
    id: listing.id,
    sfid: listing.sfid,
    propertyId: listing.propertyId,
    workspaceId: listing.workspaceId,
    buildingId: listing.buildingId,
    assets: slimListingAssets(listing.assets),
    webPrimaryKeyword: listing.webPrimaryKeyword,
    partnerLogo: listing.partnerLogo,
    workspaceProviderName: listing.workspaceProviderName,
    isPrivateProvider: listing.isPrivateProvider,
    workspaceProvider:
      workspaceProvider && typeof workspaceProvider === "object"
        ? {
          isPrivateSpaceAccount: workspaceProvider.isPrivateSpaceAccount,
          name: workspaceProvider.name,
        }
        : workspaceProvider,
    specialOffer: listing.specialOffer,
    websiteUniqueOffer: listing.websiteUniqueOffer,
    buildingName: listing.buildingName,
    address: listing.address,
    name: listing.name,
    addressFormula: listing.addressFormula,
    minDesks: listing.minDesks,
    maxDesks: listing.maxDesks,
    maxAvailableDesks: listing.maxAvailableDesks,
    noOfWS: listing.noOfWS,
    propertyType__c: listing.propertyType__c,
    spaceTypeFormula: listing.spaceTypeFormula,
    spaceTypeCounts: listing.spaceTypeCounts,
    closestBusStopName: listing.closestBusStopName,
    closestBusStopTravelTime: listing.closestBusStopTravelTime,
    closestTrainStationName: listing.closestTrainStationName,
    closestTrainStationTravelTime: listing.closestTrainStationTravelTime,
    currencyIsoCode: listing.currencyIsoCode,
    avgMonthlyCostPerWorkstation: listing.avgMonthlyCostPerWorkstation,
    priceMonthlyMin: listing.priceMonthlyMin,
    priceMonthly: listing.priceMonthly,
    advertisedPriceMonthly: listing.advertisedPriceMonthly,
    propertyTotalActiveListings: listing.propertyTotalActiveListings,
    mohLastModifiedDate: listing.mohLastModifiedDate,
    country: listing.country,
    hideStreetNumber: listing.hideStreetNumber,
    streetNumber: listing.streetNumber,
    streetName: listing.streetName,
    city: listing.city,
    state: listing.state,
    state_picklist__c: listing.state_picklist__c,
    zipcode: listing.zipcode,
    isfeatured: listing.isfeatured,
    isFeatured: listing.isFeatured,
    geolocationLat: listing.geolocationLat ?? listing.lat,
    geolocationLong: listing.geolocationLong ?? listing.lng,
    buildingData: slimBuildingData(listing.buildingData),
    workspaceData: slimWorkspaceData(listing.workspaceData),
    propertyData: slimPropertyData(listing.propertyData),
  };
}

export type GlobalPriceTierBand = {
  count?: number;
  minCost: number;
  maxCost: number;
};

export type GlobalPriceTier = Partial<{
  silver: GlobalPriceTierBand;
  gold: GlobalPriceTierBand;
  platinum: GlobalPriceTierBand;
}> & {
  /** Listing currency for tier costs — taken from property `currencyIsoCode`. */
  currencyIsoCode?: string;
};

export type GlobalPriceTierName = "silver" | "gold" | "platinum";

const GLOBAL_PRICE_TIER_KEYS: GlobalPriceTierName[] = [
  "silver",
  "gold",
  "platinum",
];

function readTierPrice(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function normalizeCurrencyIsoCode(
  value?: string | null,
): string | undefined {
  if (!value) return undefined;
  const code = String(value).trim().toUpperCase();
  return code || undefined;
}

function readTierBand(value: unknown): GlobalPriceTierBand | null {
  if (value == null) return null;

  // Legacy format: silver/gold/platinum were plain starting prices.
  if (typeof value === "number" || typeof value === "string") {
    const minCost = readTierPrice(value);
    if (minCost == null) return null;
    return { minCost, maxCost: minCost };
  }

  if (typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const minCost = readTierPrice(
    record.minCost ?? record.min_cost ?? record.min ?? record.MinCost,
  );
  const maxCost = readTierPrice(
    record.maxCost ?? record.max_cost ?? record.max ?? record.MaxCost,
  );
  if (minCost == null && maxCost == null) return null;

  const countRaw = record.count ?? record.Count;
  const count =
    countRaw == null || countRaw === ""
      ? undefined
      : Number(countRaw);

  return {
    minCost: minCost ?? maxCost!,
    maxCost: maxCost ?? minCost!,
    ...(Number.isFinite(count) && (count as number) >= 0
      ? { count: count as number }
      : {}),
  };
}

function extractCurrencyIsoCodeFromPayload(raw: unknown): string | undefined {
  const candidates = [
    raw,
    (raw as any)?.data,
    (raw as any)?.result,
    (raw as any)?.payload,
    (raw as any)?.data?.data,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const direct = normalizeCurrencyIsoCode(
      (candidate as any).currencyIsoCode ??
      (candidate as any).currency_iso_code ??
      (candidate as any).currency,
    );
    if (direct) return direct;

    const collections = [
      (candidate as any).properties,
      (candidate as any).pins,
      (candidate as any).data,
      (candidate as any).listings,
    ];
    for (const collection of collections) {
      if (!Array.isArray(collection)) continue;
      for (const item of collection) {
        const code = normalizeCurrencyIsoCode(
          item?.currencyIsoCode ??
          item?.currency_iso_code ??
          item?.currency,
        );
        if (code) return code;
      }
    }
  }

  return undefined;
}

/** Starting prices by tier from the `onlyPins=true` geo-search response. */
export function extractGlobalPriceTier(raw: unknown): GlobalPriceTier | null {
  if (!raw || typeof raw !== "object") return null;

  const candidates = [
    raw,
    (raw as any)?.data,
    (raw as any)?.result,
    (raw as any)?.payload,
    (raw as any)?.data?.data,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const source =
      (candidate as any).globalPriceTier ??
      (candidate as any).global_price_tier ??
      null;
    if (!source || typeof source !== "object") continue;

    const silver = readTierBand(source.silver ?? source.Silver);
    const gold = readTierBand(source.gold ?? source.Gold);
    const platinum = readTierBand(source.platinum ?? source.Platinum);
    if (silver == null && gold == null && platinum == null) continue;

    const result: GlobalPriceTier = {};
    if (silver != null) result.silver = silver;
    if (gold != null) result.gold = gold;
    if (platinum != null) result.platinum = platinum;

    const currencyIsoCode =
      normalizeCurrencyIsoCode(
        (source as any).currencyIsoCode ??
        (source as any).currency_iso_code ??
        (source as any).currency,
      ) || extractCurrencyIsoCodeFromPayload(raw);
    if (currencyIsoCode) result.currencyIsoCode = currencyIsoCode;

    return result;
  }

  return null;
}

/** Resolve the listing currency for tier costs (prefer API `currencyIsoCode`). */
export function resolveGlobalPriceTierCurrency(
  globalPriceTier?: GlobalPriceTier | null,
  mapPins?: Array<{ currencyIsoCode?: string }> | null,
  fallbackCurrency?: string,
): string | undefined {
  return (
    normalizeCurrencyIsoCode(globalPriceTier?.currencyIsoCode) ||
    normalizeCurrencyIsoCode(
      mapPins?.find((pin) => pin?.currencyIsoCode)?.currencyIsoCode,
    ) ||
    normalizeCurrencyIsoCode(fallbackCurrency)
  );
}

const MAP_TIER_TO_GLOBAL_KEY: Record<
  "Silver" | "Gold" | "Platinum",
  GlobalPriceTierName
> = {
  Silver: "silver",
  Gold: "gold",
  Platinum: "platinum",
};

/** Tiers present in the API payload (count > 0 when provided). */
export function getGlobalPriceTierPresent(
  globalPriceTier: GlobalPriceTier | null | undefined,
): Array<"Silver" | "Gold" | "Platinum"> {
  if (!globalPriceTier) return [];
  return (["Silver", "Gold", "Platinum"] as const).filter((tier) => {
    const band = globalPriceTier[MAP_TIER_TO_GLOBAL_KEY[tier]];
    if (!band) return false;
    if (band.count != null && band.count <= 0) return false;
    return true;
  });
}

/**
 * Price filter for a subset of selected map tiers.
 * Returns the lowest selected-tier minCost, and an upper bound when higher
 * available tiers are deselected (so "Starter only" does not mean "99+").
 * Returns `null` when all available tiers are selected (or none) — caller should
 * clear / skip the price filter.
 */
export function getSelectedTiersPriceFilter(
  globalPriceTier: GlobalPriceTier | null | undefined,
  selectedTiers: Array<"Silver" | "Gold" | "Platinum">,
  availableTiers: Array<"Silver" | "Gold" | "Platinum">,
): { minPrice: number; maxPrice?: number } | null {
  if (!globalPriceTier || availableTiers.length === 0) return null;

  const tierOrder = ["Silver", "Gold", "Platinum"] as const;
  const available = tierOrder.filter((tier) => availableTiers.includes(tier));
  const selected = tierOrder.filter(
    (tier) => available.includes(tier) && selectedTiers.includes(tier),
  );
  if (selected.length === 0) return null;

  const allSelected = available.every((tier) => selected.includes(tier));
  if (allSelected) return null;

  let minPrice: number | undefined;
  for (const tier of selected) {
    const band = globalPriceTier[MAP_TIER_TO_GLOBAL_KEY[tier]];
    const lo = readTierPrice(band?.minCost);
    if (lo != null) {
      minPrice = minPrice == null ? lo : Math.min(minPrice, lo);
    }
  }
  if (minPrice == null) return null;

  const highestSelected = selected[selected.length - 1];
  const highestIdx = tierOrder.indexOf(highestSelected);
  const nextHigher = tierOrder
    .slice(highestIdx + 1)
    .find((tier) => available.includes(tier));

  if (!nextHigher) {
    return { minPrice };
  }

  const nextMin = readTierPrice(
    globalPriceTier[MAP_TIER_TO_GLOBAL_KEY[nextHigher]]?.minCost,
  );
  if (nextMin == null || nextMin <= minPrice) {
    return { minPrice };
  }

  return { minPrice, maxPrice: nextMin - 1 };
}

/**
 * Reverse of `getSelectedTiersPriceFilter`: which legend checkboxes should stay
 * checked for a tier-driven price range.
 * `toFilterCurrency` converts each band minCost into the same units as the URL prices.
 */
export function getSelectedTiersFromPriceRange(
  globalPriceTier: GlobalPriceTier | null | undefined,
  availableTiers: Array<"Silver" | "Gold" | "Platinum">,
  minPrice: number,
  maxPrice: number | null | undefined,
  toFilterCurrency: (bandMinCost: number) => number | null,
): Array<"Silver" | "Gold" | "Platinum"> | null {
  if (!globalPriceTier || availableTiers.length === 0) return null;
  if (!Number.isFinite(minPrice) || minPrice <= 0) return null;

  const hasMax = maxPrice != null && Number.isFinite(maxPrice) && maxPrice > 0;

  const selected = availableTiers.filter((tier) => {
    const band = globalPriceTier[MAP_TIER_TO_GLOBAL_KEY[tier]];
    const lo = readTierPrice(band?.minCost);
    if (lo == null) return false;
    const converted = toFilterCurrency(lo);
    if (converted == null || !Number.isFinite(converted)) return false;
    // Allow small rounding drift on the lower bound from currency conversion.
    // Upper bound is exclusive of the next tier's min (maxPrice = nextMin - 1).
    if (converted + 1 < minPrice) return false;
    if (hasMax && converted > Number(maxPrice)) return false;
    return true;
  });

  if (!selected.length) return null;
  return selected;
}

/** @deprecated Prefer getSelectedTiersFromPriceRange */
export function getSelectedTiersFromMinPrice(
  globalPriceTier: GlobalPriceTier | null | undefined,
  availableTiers: Array<"Silver" | "Gold" | "Platinum">,
  minPrice: number,
  toFilterCurrency: (bandMinCost: number) => number | null,
): Array<"Silver" | "Gold" | "Platinum"> | null {
  return getSelectedTiersFromPriceRange(
    globalPriceTier,
    availableTiers,
    minPrice,
    null,
    toFilterCurrency,
  );
}

/** Lowest map-tier starting price (API minCost first, else min per-desk among tiered pins). */
export function getMapTierStartingPrice(
  globalPriceTier: GlobalPriceTier | null | undefined,
  mapPins?: unknown[] | null,
  currencyIsoCode = "AUD",
): { amount: number; currencyIsoCode: string } | undefined {
  const resolvedCurrency =
    resolveGlobalPriceTierCurrency(
      globalPriceTier,
      mapPins as Array<{ currencyIsoCode?: string }> | null,
      currencyIsoCode,
    ) || currencyIsoCode;

  if (globalPriceTier) {
    const values = GLOBAL_PRICE_TIER_KEYS.map(
      (key) => globalPriceTier[key]?.minCost,
    )
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (values.length > 0) {
      return { amount: Math.min(...values), currencyIsoCode: resolvedCurrency };
    }
  }

  let minFromPins: number | undefined;
  for (const pin of mapPins || []) {
    if (!pin || typeof pin !== "object") continue;
    const record = pin as Record<string, unknown>;
    const tier = record.priceTier ?? record.price_tier;
    if (!tier) continue;
    const raw =
      record.avgMonthlyCostPerWorkstation ?? record.costPerWorkstation;
    const price = Number(raw);
    if (!Number.isFinite(price) || price <= 0) continue;
    if (minFromPins === undefined || price < minFromPins) minFromPins = price;
  }
  if (minFromPins !== undefined) {
    return { amount: minFromPins, currencyIsoCode: resolvedCurrency };
  }

  return undefined;
}

export function normalizeMapPinsData(raw: any) {
  const candidates = [raw, raw?.data, raw?.result, raw?.payload].filter(Boolean);
  for (const candidate of candidates) {
    if (Array.isArray(candidate?.pins)) {
      return candidate.pins.map(normalizeMapPinRecord);
    }
    if (Array.isArray(candidate?.data)) {
      return candidate.data.map(normalizeMapPinRecord);
    }
    if (Array.isArray(candidate?.properties)) {
      return candidate.properties.map(normalizeMapPinRecord);
    }
    if (Array.isArray(candidate?.listings)) {
      return candidate.listings.map(normalizeMapPinRecord);
    }
  }
  if (Array.isArray(raw?.pins)) {
    return raw.pins.map(normalizeMapPinRecord);
  }
  return [];
}

function readPolygonTotal(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function extractPolygonTotals(...sources: unknown[]) {
  let maxTotalProperties = 0;
  let maxTotalListings = 0;

  for (const source of sources) {
    if (!source || typeof source !== "object" || Array.isArray(source)) continue;
    const record = source as Record<string, unknown>;
    const totalProperties = readPolygonTotal(record.totalProperties);
    const totalListings = readPolygonTotal(record.totalListings);

    if (totalProperties !== null) {
      maxTotalProperties = Math.max(maxTotalProperties, totalProperties);
    }
    if (totalListings !== null) {
      maxTotalListings = Math.max(maxTotalListings, totalListings);
    }
  }

  const totalProperties = maxTotalProperties || maxTotalListings || 0;
  const totalListings = maxTotalListings || maxTotalProperties || 0;

  return { totalProperties, totalListings };
}

export function normalizePolygonSearchData(raw: any, pinsRaw?: unknown) {
  const candidates = [raw, raw?.data, raw?.result, raw?.payload].filter(Boolean);
  const responseTotals = extractPolygonTotals(...candidates);
  const pinsCandidates = pinsRaw
    ? [pinsRaw, (pinsRaw as any)?.data, (pinsRaw as any)?.result, (pinsRaw as any)?.payload].filter(Boolean)
    : [];
  const pinsTotals = pinsCandidates.length ? extractPolygonTotals(...pinsCandidates) : null;

  for (const candidate of candidates) {
    const listings = Array.isArray(candidate?.data)
      ? candidate.data
      : Array.isArray(candidate?.listings)
        ? candidate.listings
        : Array.isArray(candidate?.properties)
          ? candidate.properties
          : null;

    if (!listings) continue;

    // Result copy ("Showing 1 - 30 of N") must follow the listings payload.
    // The pins call can differ by one when a listing has no map coordinate.
    const totalProperties =
      responseTotals.totalProperties ||
      responseTotals.totalListings ||
      pinsTotals?.totalProperties ||
      0;
    const totalListings =
      responseTotals.totalListings || responseTotals.totalProperties || 0;

    return {
      data: listings.map(slimSearchListingRecord),
      totalProperties,
      totalListings,
      visibleProperties: candidate?.visibleProperties,
      autoReCalibratedData: Array.isArray(candidate?.autoReCalibratedData)
        ? candidate.autoReCalibratedData.map(slimSearchListingRecord)
        : [],
    };
  }

  return {
    data: [],
    totalListings: pinsTotals?.totalListings || 0,
    totalProperties: pinsTotals?.totalProperties || 0,
    autoReCalibratedData: [],
  };
}

export type LocationPolygonLocationParams = {
  state?: string;
  city?: string;
  placeId?: string;
  text?: string;
  /** Originating page path — forwarded so the backend can record WHICH url
   * triggered a Google call on the places_cache entry (cost/abuse tracing). */
  srcUrl?: string;
};

export async function fetchLocationPolygonPair(options: {
  getLocationPolygon: (query: string) => Promise<unknown>;
  country: string;
  apiParams: Record<string, string | number>;
  searchParams: URLSearchParams;
  viewportDriven: boolean;
  location?: LocationPolygonLocationParams;
  listingsPerPage: number;
}) {
  const {
    getLocationPolygon,
    country,
    apiParams,
    searchParams,
    viewportDriven,
    location,
    listingsPerPage,
  } = options;

  const pinsFilterKeys = viewportDriven
    ? [...BASE_POLYGON_FILTER_KEYS.filter((key) => key !== "page"), ...VIEWPORT_POLYGON_KEYS]
    : BASE_POLYGON_FILTER_KEYS.filter((key) => key !== "page");
  const listingsFilterKeys = viewportDriven
    ? [...BASE_POLYGON_FILTER_KEYS, ...VIEWPORT_POLYGON_KEYS]
    : [...BASE_POLYGON_FILTER_KEYS];

  const appendPolygonFilters = (
    params: URLSearchParams,
    keys: readonly string[],
    includePerPage: boolean,
  ) => {
    keys.forEach((key) => {
      const value = apiParams[key] ?? searchParams.get(key);
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        params.set(key, String(value));
      }
    });
    if (includePerPage) {
      params.set("perPage", String(listingsPerPage));
    }
  };

  const buildBasePolygonParams = () => {
    const params = new URLSearchParams();
    params.set("country", country);
    // Page path for cache tracing — sent on every polygon call (pins + listings).
    if (location?.srcUrl) params.set("srcUrl", location.srcUrl);
    if (location?.state) params.set("state", location.state);
    if (location?.city) params.set("city", location.city);
    if (!viewportDriven) {
      if (location?.placeId) params.set("placeId", location.placeId);
      if (location?.text) params.set("text", location.text);
    }
    return params;
  };

  const pinsParams = buildBasePolygonParams();
  appendPolygonFilters(pinsParams, pinsFilterKeys, false);
  pinsParams.set("onlyPins", "true");

  const listingsParams = buildBasePolygonParams();
  appendPolygonFilters(listingsParams, listingsFilterKeys, true);
  listingsParams.set("onlyPins", "false");

  // One call must NOT take the other down. The pins call carries every marker
  // in the market — thousands on a country page — and when it fails the
  // listings are still perfectly good, so the page should show properties with
  // no map markers rather than nothing at all. `Promise.all` rejected both, the
  // loader's catch blanked everything, and nothing was logged: that is exactly
  // how /us/office-space-united-states came to render empty with no error.
  const [pinsSettled, listingsSettled] = await Promise.allSettled([
    getLocationPolygon(`?${pinsParams.toString()}`),
    getLocationPolygon(`?${listingsParams.toString()}`),
  ]);

  if (pinsSettled.status === "rejected") {
    console.error("[location-polygon] pins call failed:", pinsSettled.reason);
  }
  if (listingsSettled.status === "rejected") {
    console.error("[location-polygon] listings call failed:", listingsSettled.reason);
  }

  const pinsResponse = pinsSettled.status === "fulfilled" ? pinsSettled.value : null;
  const listingsResponse =
    listingsSettled.status === "fulfilled" ? listingsSettled.value : null;

  return {
    pinsResponse,
    listingsResponse,
    mapPins: normalizeMapPinsData(pinsResponse),
    globalPriceTier: extractGlobalPriceTier(pinsResponse),
  };
}
